<template>
  <section
    class="game-pane"
    :class="[`game-pane--${product}`, { 'game-pane--wide': wide, 'game-pane--focused': focused }]"
  >
    <!--
      In the split view this wrapper is `display: contents`, so the bands below
      stack exactly as they always did. In the single view it becomes the left
      column and the body becomes the right one.
    -->
    <header v-if="$slots.title" class="game-pane__title">
      <slot name="title" />
    </header>

    <div class="game-pane__main">
      <div class="game-pane__video">
        <slot name="video" />
      </div>

      <div v-if="$slots.info" class="game-pane__info">
        <slot name="info" />
      </div>

      <!-- Single view only: the road charts, which the split view leaves in the body. -->
      <div v-if="wide && $slots.aside" class="game-pane__aside">
        <slot name="aside" />
      </div>

      <div v-if="$slots.selector" class="game-pane__selector">
        <slot name="selector" />
      </div>
    </div>

    <div class="game-pane__body">
      <slot />
    </div>
  </section>
</template>

<script>
/*
 * The shell both desktop panes share.
 *
 * Tài Xỉu and cockfight are different games, but the design gives them an
 * identical head -- title, video, info banner, selector chips -- at identical
 * offsets, and that is what this component pins down so the two panes line up
 * band-for-band when placed side by side.
 *
 * Below the selector they diverge in *composition*, not just content:
 * `tai-xiu/bet-board.vue` in `full` mode is a monolith that already draws its
 * own markets, action bar and road charts, while cockfight builds those from
 * separate `bet-panel` and `charts` components. Prising the board apart to fake
 * a shared lower skeleton would mean refactoring the same component mobile full
 * mode and quick play depend on, so the body is deliberately one open slot and
 * each pane composes it its own way.
 *
 * Layout only: it owns no game state and dispatches nothing.
 */
export default {
  name: 'GamePane',
  props: {
    // Drives the per-product accent only; the layout is identical either way.
    product: {
      type: String,
      default: 'cockfight',
      validator: (value) => ['cockfight', 'tai-xiu'].includes(value),
    },
    focused: {
      type: Boolean,
      default: false,
    },
    /*
     * Single view: one pane fills the row, split into two columns -- video,
     * info, roads and the arena rail on the left, the betting on the right.
     * This is the shape the v1 desktop cockfight page has always had.
     */
    wide: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss" scoped>
/*
 * v2.1 prototype (1440x900 frame). The panes share the 1125px column, 9px
 * apart, and are the body row's full 828 tall. Inside: a 10px inset and every
 * band 6px apart -- title 24, video 16:9 of the content width, info bar 34,
 * arena tabs 30, then the body, which each pane composes at fixed heights
 * (board, chip row 48, roads take the rest).
 */
$pane-inset: 10px;
$band-gap: 6px;
$title-height: 24px;
$info-height: 34px;
$selector-height: 30px;

.game-pane {
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  box-sizing: border-box;
  padding: $pane-inset;
  display: flex;
  flex-direction: column;
  gap: $band-gap;
  border-radius: 12px;
  border: 1px solid $ga-border-subtle;
  background-color: $ga-pane-bg;
  overflow: hidden;
}

.game-pane__title {
  flex: 0 0 $title-height;
  height: $title-height;
  display: flex;
  align-items: center;
}

.game-pane__video {
  // 16:9 of the content width (the players hold their own aspect ratio).
  flex: 0 0 auto;
  // Rounded on top only: the info bar sits flush underneath and carries the
  // bottom corners, so the two read as one block.
  border-radius: 10px 10px 0 0;
  overflow: hidden;
  background-color: $ga-rail-bg;
}

.game-pane__info {
  flex: 0 0 $info-height;
  height: $info-height;
  min-height: $info-height;
  // Flush under the video: the pane's band gap does not apply between the two.
  margin-top: -$band-gap;
  display: flex;
  align-items: center;
}

.game-pane__selector {
  flex: 0 0 $selector-height;
  height: $selector-height;
  min-height: $selector-height;
  display: flex;
  align-items: center;
  // Scroll rather than clip: an arena list longer than the pane was simply
  // being cut off at the right edge with no way to reach the rest.
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.game-pane__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: $band-gap;
  // Everything here is sized to fit; nothing scrolls.
  overflow: hidden;
}

/*
 * Split view: the wrapper carries no box of its own, so the bands are still
 * direct children of the pane's own column.
 */
.game-pane__main {
  display: contents;
}

/*
 * Single view. One pane fills the column, title on its own row, then two
 * columns: video, info bar and roads on the left, betting on the right. The
 * left column is exactly a split-view pane's content box -- (1125 - 9) / 2 -
 * 2 x 11 = 536 -- so the video neither moves nor resizes when switching
 * single <-> multiple. The gap is the pane's own 10 inset, so the space between
 * the columns matches the space at the pane's right edge; the betting column
 * takes the rest (1103 - 536 - 10 = 557).
 */
$split-content-width: 536px;

.game-pane--wide {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: $split-content-width minmax(0, 1fr);
  grid-template-rows: $title-height minmax(0, 1fr);
  column-gap: $pane-inset;
  row-gap: $band-gap;

  .game-pane__title {
    grid-column: 1 / -1;
    grid-row: 1;
  }

  .game-pane__main {
    grid-column: 1;
    grid-row: 2;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }

  // 16:9 of the column, as in the split view (the players hold their ratio).

  .game-pane__info {
    margin-top: 0;
  }

  .game-pane__body {
    grid-column: 2;
    grid-row: 2;
    min-width: 0;
    gap: 0;
  }

  .game-pane__aside {
    margin-top: 8px;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }
}

.game-pane--focused {
  border-color: rgba(239, 200, 127, 0.55);
}
</style>
